<template>
  <div class="flex flex-col max-w-l grow">
    <span class="text-2xl sm:text-3xl font-medium text-center sm:px-20 mb-8">
      {{ $t(`${trPrefix}setPassword`) }}
    </span>

    <WebccInput
      v-model="password"
      class="mb-4"
      name="newPassword"
      :label="t(`${trPrefix}newPassword`)"
      type="password"
    />
    <WebccInput
      v-model="confirmPassword"
      class="mb-4"
      name="confirmPassword"
      :label="t(`${trPrefix}confirmPassword`)"
      type="password"
      :valid="passwordValidity"
      @keyup.enter.stop="setNewPw"
    />

    <div
      class="flex w-full items-center"
      :class="errorMessage ? 'mb-4' : 'mb-6'"
    >
      <template v-if="confirmPassword.length === 0 || doPasswordsMatch">
        <WebccIcon
          v-if="password.length > 7"
          name="site/check"
          class="size-4 mr-1 text-suc"
        />
        <WebccIcon
          v-else-if="password.length > 0 && password.length < 8"
          name="site/error-warning"
          class="size-4 mr-1 text-err"
        />
        <WebccIcon
          v-else
          name="site/ellipse"
          class="size-1 mr-1 text-txt-weak"
        />
      </template>

      <span
        v-if="!doPasswordsMatch && confirmPassword.length > 0"
        class="text-xs text-err"
      >
        {{ $t(`${trPrefix}noMatch`) }}
      </span>

      <span
        v-else
        class="text-xs"
        :class="
          password.length > 0 && password.length < 8
            ? 'text-err'
            : 'text-txt-weak'
        "
      >
        {{ $t(`${trPrefix}passwordHint`) }}
      </span>
    </div>

    <WebccFeedbackMessage
      v-if="errorMessage"
      type="error"
      :message="errorMessage"
      class="mb-6"
    />

    <WebccButton
      class="mb-4 w-full"
      variant="theme"
      size="lg"
      :loading
      @click="setNewPw"
    >
      {{ $t(`${trPrefix}reset`) }}
    </WebccButton>
  </div>

  <UserAgreement />
</template>

<script lang="ts" setup>
const trPrefix = 'components.auth.recover.resetPasswordForm.'

const emit = defineEmits<{
  refresh: []
  success: []
}>()

const { t } = useI18n()
const auth = useAuthStore()
const user = useUserStore()

const password = ref('')
const confirmPassword = ref('')
const errorMessage = ref<string>()
const loading = ref(false)

await startFlows()

onUnmounted(() => {
  password.value = ''
  confirmPassword.value = ''
  errorMessage.value = undefined
  loading.value = false
})

async function startFlows() {
  if (!import.meta.client) return
  if (auth.isSessionExpired) {
    return await prepareRefresh()
  }

  try {
    await auth.settingsStart()
  } catch {
    await prepareRefresh()
  }
}

async function prepareRefresh() {
  try {
    await auth.logout()
  } catch {
    errorMessage.value = t(`${trPrefix}resetError`)
  }
  emit('refresh')
}

async function setNewPw() {
  if (!passwordValidity.value) return
  if (!auth.isRecoveryStarted) return
  if (auth.isSessionExpired) return

  try {
    loading.value = true

    await auth.settingsComplete(password.value)

    user.passwordChanged = true
    emit('success')
  } catch (error) {
    if (error instanceof SessionRefreshRequiredError) {
      emit('refresh') // back to the forgot password page
    }
    errorMessage.value = t(`${trPrefix}resetError`)
  } finally {
    loading.value = false
  }
}

const doPasswordsMatch = computed(
  () => password.value === confirmPassword.value,
)

/**
 * Set the validity status of the confirmPassword input field.
 * If it is not filled reset the validity to undefined.
 */
const passwordValidity = computed(() => {
  if (!password.value.length || !confirmPassword.value.length) return undefined

  return doPasswordsMatch.value
})
</script>
